/**
 * React Query hooks for the Social Planner (SPEC PX-3 — social-planner R2).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import type {
  SocialPost,
  SocialPostCreate,
  SocialPostListResponse,
  SocialPostUpdate,
} from '@/types/socialPost';
import {
  listSocialPosts,
  createSocialPost,
  updateSocialPost,
  deleteSocialPost,
  publishSocialPost,
} from '../socialPosts';

export const socialPostKeys = {
  all: ['socialPosts'] as const,
  list: () => [...socialPostKeys.all, 'list'] as const,
  detail: (id: string) => [...socialPostKeys.all, id] as const,
};

export function useSocialPosts(): UseQueryResult<SocialPostListResponse, Error> {
  return useQuery({
    queryKey: socialPostKeys.list(),
    queryFn: listSocialPosts,
  });
}

export function useCreateSocialPost(): UseMutationResult<SocialPost, Error, SocialPostCreate, unknown> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSocialPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialPostKeys.list() });
    },
  });
}

export function useUpdateSocialPost(): UseMutationResult<
  SocialPost,
  Error,
  { id: string; data: SocialPostUpdate },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => updateSocialPost(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialPostKeys.list() });
    },
  });
}

export function useDeleteSocialPost(): UseMutationResult<
  { status: string; post_id: string },
  Error,
  string,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSocialPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialPostKeys.list() });
    },
  });
}

export function usePublishSocialPost(): UseMutationResult<
  Record<string, unknown>,
  Error,
  string,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishSocialPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialPostKeys.list() });
    },
  });
}
